import React from 'react';
import { useSelector } from 'react-redux';
import { Images } from 'utils';
import { GoalModePicker, useGoalModeGoals } from './goalModeField';
import { TARGET_BLOCK_ATTR, fillGoalBlockGoal } from '../../../utils/inlineFields';
// Circular with inlineFieldsRichText.js (which draws this block); both are only used at render time.
import { InlineFieldsRichText } from './inlineFieldsRichText';

const sameIds = (a = [], b = []) => a.length === b.length && a.every((id) => b.includes(id));

/**
 * A progress report Goal block on the fill surface: "+ Add Goal" (the same
 * picker Goal Mode uses, limited to the block's goal types, with each goal's
 * targets to pick from when the goal design has a Target block) and the picked
 * goals as the block's designs filled in.
 *
 * Goals and their tokens come with the report's default values
 * (`formDefault.GOAL_MODE_GOALS`). Each goal's html is filled when it is picked
 * and stored with the pick, so the read-only renderers need no goal data; a
 * goal kept with the same targets keeps its html.
 *
 * Inline fields from the Goal / Target designs are copied into each goal's html
 * when it is picked, and filled there (`InlineFieldsRichText`, one per goal);
 * `showErrors` / `hideStaffOnly` pass through to them.
 *
 * `onChange({ goals })` receives the new picks.
 */
export const GoalBlockFill = ({ block, value, onChange, disabled, error, showErrors, hideStaffOnly, imageUrls }) => {
    const formDefault = useSelector((state) => state.forms.formDefault);
    const goals = useGoalModeGoals();
    const picked = value?.goals || [];
    const withTargets = String(block?.goalDesign || '').includes(TARGET_BLOCK_ATTR);
    const isDisabled = disabled || !!block?.properties?.readOnly;

    const handleDone = (goalIds, targetIds = []) => {
        const next = goalIds
            .map((goalId) => {
                const goal = goals.find((g) => g.id === goalId);
                if (!goal) return picked.find((p) => p.id === goalId);
                const targets = withTargets ? (goal.targets || []).filter((target) => targetIds.includes(target.id)) : [];
                const pickedTargetIds = targets.map((target) => target.id);
                const kept = picked.find((p) => p.id === goalId && sameIds(p.targetIds, pickedTargetIds));
                if (kept) return kept;
                const { targets: _targets, ...goalTokens } = goal;
                return { id: goalId, targetIds: pickedTargetIds, html: fillGoalBlockGoal(block, { ...formDefault, ...goalTokens }, targets) };
            })
            .filter(Boolean);
        onChange({ goals: next });
    };

    return (
        <div className={`goal-block-fill${error ? ' goal-block-fill-error' : ''}`}>
            <div className="goal-block-fill-header">
                <img src={Images.attributeGoal} alt="" />
                <b>{block?.label || 'Goals'}</b>
                {block?.properties?.required && <span className="inline-field-required">*</span>}
                {!!picked.length && <span className="goal-block-fill-count">· {picked.length === 1 ? '1 goal' : `${picked.length} goals`}</span>}
                {!isDisabled && (
                    <span className="goal-block-fill-action">
                        <GoalModePicker
                            selectedIds={picked.map((p) => p.id)}
                            onDone={handleDone}
                            withTargets={withTargets}
                            selectedTargetIds={picked.flatMap((p) => p.targetIds || [])}
                            goalTypes={block?.goalTypes}
                        />
                    </span>
                )}
            </div>
            {picked.length ? (
                <div className="goal-block-fill-body">
                    {picked.map((goal) => (
                        <InlineFieldsRichText
                            key={goal.id}
                            html={goal.html || ''}
                            disabled={disabled}
                            showErrors={showErrors}
                            hideStaffOnly={hideStaffOnly}
                            imageUrls={imageUrls}
                            onChange={(html) => onChange({ goals: picked.map((p) => (p.id === goal.id ? { ...p, html } : p)) })}
                        />
                    ))}
                </div>
            ) : (
                <p className="goal-mode-picker-empty">
                    {withTargets ? 'Select goals and targets with + Add Goal to see their data here.' : 'Select goals with + Add Goal to see their data here.'}
                </p>
            )}
        </div>
    );
};
